import React, { useEffect, useState } from 'react'
import { FloatingInspectorButton, FloatingInspectorButtonProps, MainText } from '../FloatingInspectorButton'
import { useTranslation } from 'react-i18next'
import { lightTheme } from '@/customTheme'
import Icons from '../../Icons'
import FloatingInspectorButtonIcon from './FloatingInspectorButtonIcon'

export interface FloatingInspectorButtonIconAndTextProps
  extends Pick<
    FloatingInspectorButtonProps,
    'onClick' | 'selected' | 'disabled' | 'disabledStyle' | 'tooltipText' | 'placementTooltip' | 'marginTooltip' | 'popoverOnHover' | 'popoverContent' | 'nonInteractive' | 'style' | 'showArrow'
  > {
    text: string
    icon: React.ReactNode
    hasCrown?: boolean
    id?: string
    disableHover?: boolean
    showText?: boolean
    fontWeight?: number
}

const FloatingInspectorButtonIconAndText: React.FC<FloatingInspectorButtonIconAndTextProps> = ({
  icon,
  text,
  onClick,
  selected,
  disabled,
  disabledStyle,
  hasCrown,
  tooltipText, 
  placementTooltip, 
  marginTooltip,
  popoverOnHover,
  popoverContent,
  id,
  disableHover,
  showText,
  nonInteractive,
  fontWeight,
  style,
  showArrow,
}) => {
  const [t] = useTranslation()
  const tooltipTextOnHideText = tooltipText || (!showText && !popoverOnHover && text ? t(text) : undefined)

  // Derive the icon's disabled color from `disabled` here so callers can pass a
  // plain icon and rely on this component for the greyed-out state (mirrors
  // DefaultButton's iconComponent cloning).
  const styledIcon =
    disabled && React.isValidElement(icon)
      ? React.cloneElement(icon as React.ReactElement<{ fill?: string }>, { fill: lightTheme.colors.grayScale200 })
      : icon

  if(!showText) {
    return (
      <FloatingInspectorButtonIcon
        icon={styledIcon}
        onClick={onClick}
        tooltipText={tooltipTextOnHideText}
        disabled={disabled}
        disabledStyle={disabledStyle}
        selected={selected}
        placementTooltip={placementTooltip}
        marginTooltip={marginTooltip}
        popoverOnHover={popoverOnHover}
        popoverContent={popoverContent}
        hasCrown={hasCrown}
        id={id}
        disableHover={disableHover}
        nonInteractive={nonInteractive}
        showArrow={showArrow}
      />
    )
  }

  return (
    <FloatingInspectorButton
      onClick={onClick}
      selected={selected}
      disabled={disabled}
      disabledStyle={disabledStyle}
      style={{padding: '4px 8px', ...style}}
      tooltipText={tooltipText}
      placementTooltip={placementTooltip}
      marginTooltip={marginTooltip}
      popoverOnHover={popoverOnHover}
      popoverContent={popoverContent}
      id={id}
      disableHover={disableHover}
      nonInteractive={nonInteractive}
      showArrow={showArrow}
    >
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {styledIcon}
      </div>
      <MainText $disabled={disabled} $fontWeight={fontWeight}>{t(text)}</MainText>
      {hasCrown && <Icons.PremiumIcon disabled={disabled} />}
    </FloatingInspectorButton>
  )
}

export default FloatingInspectorButtonIconAndText